import { getRequest, postRequest, patchRequest, deleteRequest, BASE_URL } from '../utils/axios';

const withProject = (path, projectHash) => {
  if (!projectHash) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}project=${encodeURIComponent(projectHash)}`;
};

export const dashboardService = {
  projects: (onSuccess, onError) => getRequest('/api/in/projects/', onSuccess, onError),
  createProject: (data, onSuccess, onError) => postRequest('/api/in/projects/', data, onSuccess, onError),
  patchProject: (hash, data, onSuccess, onError) => patchRequest(`/api/in/projects/${hash}/`, data, onSuccess, onError),
  deleteProject: (hash, onSuccess, onError) => deleteRequest(`/api/in/projects/${hash}/`, onSuccess, onError),
  summary: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    const query = search.toString();
    getRequest(query ? `/api/in/dashboard/?${query}` : '/api/in/dashboard/', onSuccess, onError);
  },
  routing: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    const query = search.toString();
    getRequest(query ? `/api/in/routing/?${query}` : '/api/in/routing/', onSuccess, onError);
  },
  updateRouting: (projectHash, data, onSuccess, onError) =>
    postRequest(withProject('/api/in/routing/', projectHash), { ...data, project: projectHash }, onSuccess, onError),
  logs: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    const query = search.toString();
    getRequest(query ? `/api/in/logs/?${query}` : '/api/in/logs/', onSuccess, onError);
  },
  logDetail: (logHash, onSuccess, onError) => getRequest(`/api/in/logs/${logHash}/`, onSuccess, onError),
  recentEvents: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries({ page_size: 20, order: 'desc', ...(params || {}) }).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    getRequest(`/api/in/logs/?${search}`, onSuccess, onError);
  },
  models: (onSuccess, onError) => getRequest('/api/in/models/', onSuccess, onError),
  playgroundModels: (projectHash, onSuccess, onError) =>
    getRequest(withProject('/api/in/playground/', projectHash), onSuccess, onError),
  playgroundRecent: (projectHash, onSuccess, onError) =>
    getRequest(withProject('/api/in/playground/recent/', projectHash), onSuccess, onError),
  modelDetail: (model, tier, onSuccess, onError) => {
    const search = new URLSearchParams({ model, tier: tier || 'equal' });
    getRequest(`/api/in/playground/model-detail/?${search}`, onSuccess, onError);
  },
  playground: (projectHash, data, onSuccess, onError) =>
    postRequest(withProject('/api/in/playground/', projectHash), { ...data, project: projectHash }, onSuccess, onError),
  playgroundStream: async (projectHash, data, { onChunk, onThinking, onDone, onError }) => {
    const url = `${BASE_URL}${withProject('/api/in/playground/', projectHash)}`;
    const token = localStorage.getItem('accessToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ...data, project: projectHash }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      onError(err.detail || 'Request failed');
      return;
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      onDone(await response.json());
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const evt = JSON.parse(raw);
          if (evt.type === 'chunk') onChunk(evt.content || '');
          else if (evt.type === 'thinking') onThinking?.(evt.content || '');
          else if (evt.type === 'done') onDone(evt);
          else if (evt.type === 'error') onError(evt.detail || 'Stream failed');
        } catch {
          /* malformed event */
        }
      }
    }
  },
  billing: (params, onSuccess, onError) => {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    const query = search.toString();
    getRequest(query ? `/api/in/billing/?${query}` : '/api/in/billing/', onSuccess, onError);
  },
  billingCheckout: (data, onSuccess, onError) =>
    postRequest('/api/in/billing/checkout/', data, onSuccess, onError),
  billingAutoTopUp: (data, onSuccess, onError) =>
    patchRequest('/api/in/billing/auto-topup/', data, onSuccess, onError),
  getBillingAutoTopUp: (onSuccess, onError) =>
    getRequest('/api/in/billing/auto-topup/', onSuccess, onError),
  keys: (projectHash, onSuccess, onError) =>
    getRequest(withProject('/api/in/keys/', projectHash), onSuccess, onError),
  createKey: (data, onSuccess, onError) => postRequest('/api/in/keys/', data, onSuccess, onError),
  revokeKey: (hash, onSuccess, onError) => deleteRequest(`/api/in/keys/${hash}/`, onSuccess, onError),
  patchKey: (hash, data, onSuccess, onError) => patchRequest(`/api/in/keys/${hash}/`, data, onSuccess, onError),
  evalsDashboard: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    getRequest(`/api/in/evals/?${search}`, onSuccess, onError);
  },
  evalsRuns: (projectHash, params, onSuccess, onError) => {
    const search = new URLSearchParams();
    if (projectHash) search.set('project', projectHash);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value != null && value !== '') search.set(key, String(value));
    });
    getRequest(`/api/in/evals/runs/?${search}`, onSuccess, onError);
  },
  evalRunDetail: (runId, onSuccess, onError) => getRequest(`/api/in/evals/runs/${runId}/`, onSuccess, onError),
  runEval: (projectHash, data, onSuccess, onError) =>
    postRequest(withProject('/api/in/evals/run/', projectHash), { ...data, project: projectHash }, onSuccess, onError),
};
